import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, ButtonBase, Stack, Tooltip, Typography } from '@mui/material';
import { CaseIcon } from 'src/icons/CaseIcon';
import StreamPlanBadge from 'src/components/StreamPlanBadge';
import Plus from 'src/icons/Plus';
import { getStreamStatus } from 'src/utils/leadStreams';
import { streamDetailsPath, streamIdFromPath } from 'src/utils/streamUrl';
import LeadStreamsService from 'src/services/LeadStreamsService';
import EventService from 'src/services/EventService';

import {
    StreamActionsLine,
    ActionButton,
    PauseGlyph,
    PlayGlyph,
    TopUpGlyph,
    StreamItem,
    StreamList,
    StreamIcon,
    StreamName,
    StreamMeta,
    StreamMetaText,
    StreamProgressTrack,
    StreamProgressFill,
    NewStreamText,
    PlusIcon,
    getStreamMetaLabel,
    getStreamProgressValue,
    getProgressColor,
} from './LeadStreamsMenu';

import type { Stream } from 'src/services/LeadStreamsService/agent';

export default function FlowNavigation({ streams, go }: { streams: Stream[]; go: (path: string) => void }) {
    const { pathname } = useLocation();
    const [visibleStreams, setVisibleStreams] = useState(streams);
    const [updating, setUpdating] = useState<Record<number, boolean>>({});
    const [errors, setErrors] = useState<Record<number, string>>({});
    const pending = useRef(new Set<number>());
    useEffect(() => setVisibleStreams(streams), [streams]);
    const toggleActive = async (stream: Stream) => {
        if (pending.current.has(stream.id)) return;
        pending.current.add(stream.id);
        setUpdating((s) => ({ ...s, [stream.id]: true }));
        setErrors((s) => ({ ...s, [stream.id]: '' }));
        try {
            const updated = await LeadStreamsService.updateStream(stream.id, { is_active: !stream.is_active });
            setVisibleStreams((s) => s.map((item) => (item.id === updated.id ? updated : item)));
            EventService.dispatch('LEAD_STREAMS_UPDATED', updated);
        } catch (_) {
            setErrors((s) => ({ ...s, [stream.id]: 'Не удалось изменить статус. Попробуйте ещё раз.' }));
        } finally {
            pending.current.delete(stream.id);
            setUpdating((s) => ({ ...s, [stream.id]: false }));
        }
    };
    return (
        <Box sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 2 }}>
            <StreamList>
                {visibleStreams.map((singleStream) => {
                    const name = singleStream.name || singleStream.category.name;
                    const detailsUrl = streamDetailsPath(singleStream.id);
                    const status = getStreamStatus(singleStream);
                    const canToggle = singleStream && ['active', 'paused'].includes(status.key);
                    const toggleLabel = singleStream?.is_active ? 'Пауза' : 'Возобновить';
                    const checkoutLabel = 'Докупить';
                    const metric = getStreamMetaLabel(singleStream);
                    const selected = streamIdFromPath(pathname.split('/')[2] || '') === singleStream.id;
                    const progress = getStreamProgressValue(singleStream);
                    return (
                        <StreamItem
                            key={singleStream.id}
                            sx={{
                                minHeight: status.key === 'pending' ? 70 : 86,
                                border: selected ? '1px solid var(--lm-border)' : '1px solid transparent',
                                bgcolor: selected ? 'var(--lm-surface)' : undefined,
                                '& + &': {
                                    mt: 1,
                                    position: 'relative',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: -5,
                                        left: 8,
                                        right: 8,
                                        borderTop: '1px solid var(--lm-border)',
                                    },
                                },
                            }}
                            onClick={() => go(detailsUrl + (status.key === 'pending' ? '?checkout=1' : ''))}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                            >
                                <ButtonBase
                                    aria-label={name}
                                    aria-current={selected ? 'page' : undefined}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        go(detailsUrl + (status.key === 'pending' ? '?checkout=1' : ''));
                                    }}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        textAlign: 'left',
                                        justifyContent: 'start',
                                        gap: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        '&&[aria-current="page"]': { bgcolor: 'transparent' },
                                    }}
                                >
                                    <StreamIcon>{singleStream?.category.icon || <CaseIcon />}</StreamIcon>
                                    <StreamName color="text.primary">{name}</StreamName>
                                </ButtonBase>
                                {status.key !== 'pending' && (
                                    <StreamActionsLine
                                        className="stream-actions-line"
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        {canToggle && (
                                            <Tooltip title={toggleLabel}>
                                                <span>
                                                    <ActionButton
                                                        type="button"
                                                        aria-label={toggleLabel}
                                                        disabled={updating[singleStream.id]}
                                                        onClick={() => void toggleActive(singleStream)}
                                                        subtle
                                                    >
                                                        {singleStream.is_active ? <PauseGlyph /> : <PlayGlyph />}
                                                    </ActionButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        <Tooltip title={checkoutLabel}>
                                            <ActionButton
                                                type="button"
                                                aria-label={checkoutLabel}
                                                onClick={() => go(detailsUrl + '?checkout=1')}
                                                subtle={status.key !== 'ended'}
                                            >
                                                <TopUpGlyph />
                                            </ActionButton>
                                        </Tooltip>
                                    </StreamActionsLine>
                                )}
                            </Stack>
                            <StreamMeta>
                                {status.key === 'pending' ? (
                                    <ButtonBase
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            go(detailsUrl + '?checkout=1');
                                        }}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            minHeight: 28,
                                            px: 0.5,
                                            ml: -0.5,
                                            borderRadius: 1,
                                            fontSize: 13,
                                            color: 'var(--lm-muted)',
                                            '&:hover, &:focus-visible': { color: 'var(--lm-accent-ink)' },
                                            '&:focus-visible': {
                                                outline: '2px solid var(--lm-accent-ink)',
                                                outlineOffset: 2,
                                            },
                                        }}
                                    >
                                        Запустить поток
                                    </ButtonBase>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                        <StreamPlanBadge stream={singleStream} />
                                        <StreamMetaText sx={{ minWidth: 0 }}>{metric}</StreamMetaText>
                                    </Box>
                                )}
                                {status.key !== 'pending' && (
                                    <StreamProgressTrack
                                        role="progressbar"
                                        aria-label={'Остаток лидов: ' + name}
                                        aria-valuenow={Math.round(progress)}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        aria-valuetext={metric}
                                    >
                                        <StreamProgressFill
                                            color={getProgressColor(status.key)}
                                            value={progress}
                                        />
                                    </StreamProgressTrack>
                                )}
                            </StreamMeta>
                            {errors[singleStream.id] && (
                                <Typography
                                    role="alert"
                                    sx={{ fontSize: 12, color: 'var(--lm-danger-ink)' }}
                                >
                                    {errors[singleStream.id]}
                                </Typography>
                            )}
                        </StreamItem>
                    );
                })}
            </StreamList>
            <ButtonBase
                onClick={() => go('/leadstream')}
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: 1,
                    p: 1,
                    mt: 1.5,
                    borderRadius: 1,
                    '&:hover': {
                        '& .new-stream-text': { color: 'var(--lm-accent-ink)' },
                        '& .plus-icon svg path': { fill: 'var(--lm-accent-ink)' },
                    },
                }}
            >
                <NewStreamText className="new-stream-text">Новый поток</NewStreamText>
                <PlusIcon className="plus-icon">
                    <Plus />
                </PlusIcon>
            </ButtonBase>
        </Box>
    );
}
